import React, { useState, useEffect } from 'react';
import { Monitor, Copy, RefreshCw, Eye, EyeOff, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { SettingsCard } from '../ui';
import { useAuth } from '@/context/AuthContext';

interface KioskCredentials {
  id: string;
  username: string;
  password: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const KioskCredentialsSettings: React.FC = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState<KioskCredentials | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);

  // Fetch existing credentials
  useEffect(() => {
    const fetchCredentials = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('kiosk_credentials')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          // PGRST116 = no rows returned
          console.error('Error fetching kiosk credentials:', error);
        }

        setCredentials(data || null);
      } catch (err) {
        console.error('Failed to fetch kiosk credentials:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [user?.id]);

  const generateCredentials = async () => {
    if (!user?.id) return;

    setGenerating(true);
    try {
      // Call the database function to generate credentials
      const { data, error } = await supabase
        .rpc('generate_kiosk_credentials', { p_user_id: user.id });

      if (error) throw error;

      if (data && data.length > 0) {
        // Refresh credentials from database
        const { data: refreshed, error: refreshError } = await supabase
          .from('kiosk_credentials')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (refreshError) throw refreshError;

        setCredentials(refreshed);
        setShowPassword(true); // Show password after generation
        toast.success('Kiosk credentials generated successfully!');
      }
    } catch (err) {
      console.error('Failed to generate kiosk credentials:', err);
      toast.error('Failed to generate credentials');
    } finally {
      setGenerating(false);
    }
  };

  const toggleEnabled = async () => {
    if (!credentials) return;

    try {
      const newEnabled = !credentials.enabled;
      const { error } = await supabase
        .from('kiosk_credentials')
        .update({ enabled: newEnabled, updated_at: new Date().toISOString() })
        .eq('id', credentials.id);

      if (error) throw error;

      setCredentials({ ...credentials, enabled: newEnabled });
      toast.success(newEnabled ? 'Kiosk access enabled' : 'Kiosk access disabled');
    } catch (err) {
      console.error('Failed to toggle kiosk access:', err);
      toast.error('Failed to update kiosk access');
    }
  };

  const copyToClipboard = async (value: string, field: 'username' | 'password') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <SettingsCard
        icon={Monitor}
        title="Kiosk Credentials"
        description="Manage your kiosk login credentials"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      icon={Monitor}
      title="Kiosk Credentials"
      description="Manage your kiosk login credentials"
    >
      <div className="space-y-4">
        {/* Info Section */}
        <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
          <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
            Use these credentials to log into the kiosk mode at{' '}
            <a
              href="/kiosk"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline inline-flex items-center gap-1"
            >
              /kiosk <ExternalLink className="h-3 w-3" />
            </a>
            . One set of credentials gives access to all your businesses.
          </AlertDescription>
        </Alert>

        {!credentials ? (
          // No credentials yet
          <div className="text-center py-6">
            <Monitor className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No kiosk credentials configured yet
            </p>
            <Button
              onClick={generateCredentials}
              disabled={generating}
            >
              {generating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Generate Credentials
                </>
              )}
            </Button>
          </div>
        ) : (
          // Show existing credentials
          <div className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-border bg-gray-50 dark:bg-muted/50">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Kiosk Access</Label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {credentials.enabled ? 'Access is enabled' : 'Access is disabled'}
                </p>
              </div>
              <Switch
                checked={credentials.enabled}
                onCheckedChange={toggleEnabled}
              />
            </div>

            {/* Username Field */}
            <div className="space-y-2">
              <Label htmlFor="kiosk-username" className="text-sm font-medium">Username</Label>
              <div className="flex gap-2">
                <Input
                  id="kiosk-username"
                  value={credentials.username}
                  readOnly
                  className="font-mono bg-gray-50 dark:bg-muted"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(credentials.username, 'username')}
                  title="Copy username"
                >
                  {copiedField === 'username' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="kiosk-password" className="text-sm font-medium">Password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="kiosk-password"
                    type={showPassword ? 'text' : 'password'}
                    value={credentials.password}
                    readOnly
                    className="font-mono bg-gray-50 dark:bg-muted pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(credentials.password, 'password')}
                  title="Copy password"
                >
                  {copiedField === 'password' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Regenerate Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-border">
              <Button
                variant="outline"
                onClick={generateCredentials}
                disabled={generating}
                className="w-full"
              >
                {generating ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Regenerate Credentials
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                This will create new username and password
              </p>
            </div>
          </div>
        )}
      </div>
    </SettingsCard>
  );
};
